/**
 * 基本白 / 基本黑 — the Switch's own two system themes.
 *
 * The slideshow stage is always pure black regardless: photos need a neutral surround, and a light
 * frame around a photo is a colour cast. This only governs the browsing chrome (album grid, file
 * list, admin, join).
 *
 * Deciding the theme before first paint has to happen in CSS, not here. The obvious fix — a tiny
 * inline `<script>` in each entry's `<head>` — is impossible: the response CSP is `script-src 'self'`
 * with no `'unsafe-inline'` and no nonce, so the browser would refuse to run it and every page would
 * boot light. Relaxing the CSP to buy a theme flash would be a bad trade.
 *
 * So base.css carries a `@media (prefers-color-scheme: dark)` block that paints dark immediately for
 * anyone whose OS is dark, and this module only takes over once the bundle is running — writing an
 * explicit `data-theme`, which outranks the media query on specificity. The only case that can still
 * flash is a user who deliberately chose the opposite of their OS setting, and it lasts one frame.
 */

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'fdpf.theme';

/** Kept in sync with `--bg` in base.css; drives the iOS status bar and the PWA splash. */
const THEME_COLOR: Record<Theme, string> = {
  light: '#ebebeb',
  dark: '#2d2d2d',
};

function systemTheme(): Theme {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

export function storedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    // Private browsing on old iOS throws on localStorage access rather than returning null.
    return null;
  }
}

export function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'light' || attr === 'dark') return attr;
  return storedTheme() ?? systemTheme();
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);

  // Without this the iOS status bar and the area behind a rubber-band overscroll keep the old
  // colour, which looks like a rendering bug on a home-screen app.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Choice just won't persist; the UI still switches for this session.
  }
}

export function toggleTheme(): Theme {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/**
 * Follows the OS until the user picks a side explicitly. Once they have chosen, their choice wins
 * forever — a frame that flipped to dark on its own at sunset because iOS did would read as a bug.
 */
export function installTheme(): void {
  applyTheme(currentTheme());
  if (typeof window.matchMedia !== 'function') return;

  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (storedTheme() === null) applyTheme(systemTheme());
  };

  // Safari did not support addEventListener on MediaQueryList until 14; addListener is deprecated
  // but is the only thing that works on the iOS 12/13 devices this has to run on.
  if (typeof query.addEventListener === 'function') query.addEventListener('change', onChange);
  else if (typeof query.addListener === 'function') query.addListener(onChange);
}
